import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { Link } from 'react-router-dom';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import LazyImage from '../../components/base/LazyImage';
import { 
  createArticleStructuredData, 
  createFAQStructuredData, 
  createBreadcrumbStructuredData,
  createHowToStructuredData
} from '../../utils/structuredData';

export default function MinimumAmountPage() {
  // SEO設定
  usePageSEO({
    title: 'FXは少額いくらから始められる？初心者向け資金ガイド',
    description: 'FXは数千円〜1万円程度の少額から始められます。レバレッジの仕組み、少額取引のメリット・注意点、初心者におすすめの1,000通貨対応口座を詳しく解説。余裕資金で安全にFXを始める方法を紹介します。',
    keywords: 'FX,少額,いくらから,初心者,資金,レバレッジ,1000通貨,証拠金,始め方',
    structuredData: [
      createArticleStructuredData({
        headline: 'FXは少額いくらから始められる？初心者向け資金ガイド',
        description: 'FXに必要な最低資金、少額取引のメリット・注意点、初心者向け口座選びを徹底解説。数千円から始められるFXの仕組みを分かりやすく説明します。',
        author: 'DMM FX情報局',
        datePublished: '2024-11-20',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/0b0055bed939ced99f3ad74b353da9b4.jpg',
        url: '/minimum-amount',
      }),
      createHowToStructuredData({
        name: '少額でFXを始める方法',
        description: '初心者が少額で安全にFXを始めるための4つのステップを解説します。',
        totalTime: 'PT30M',
        estimatedCost: '数千円〜1万円',
        steps: [
          {
            name: '1,000通貨対応口座を選ぶ',
            text: '少額取引に対応したFX会社で口座を開設します。1,000通貨単位なら必要資金を抑えられます。',
            image: 'https://readdy.ai/api/search-image?query=online%20account%20selection%20comparison%20choosing%20broker%20simple%20clean%20interface&width=800&height=600&seq=minimum-step1-001&orientation=landscape',
            url: '/minimum-amount',
          },
          {
            name: '余裕資金を準備する',
            text: '生活費とは別の余裕資金を用意します。初心者は1〜3万円程度から始めるのがおすすめです。',
            image: 'https://readdy.ai/api/search-image?query=savings%20money%20preparation%20budget%20planning%20financial%20safety%20clean%20modern%20design&width=800&height=600&seq=minimum-step2-001&orientation=landscape',
            url: '/minimum-amount',
          },
          {
            name: 'レバレッジを抑える',
            text: '最初は5倍以下の低レバレッジで取引します。リスクを抑えながら経験を積めます。',
            image: 'https://readdy.ai/api/search-image?query=risk%20management%20control%20safety%20lever%20balance%20careful%20trading%20clean%20professional%20design&width=800&height=600&seq=minimum-step3-001&orientation=landscape',
            url: '/leverage',
          },
          {
            name: '損切りルールを決める',
            text: '事前に損失許容額を決めて、ルールを守って取引します。感情的な判断を避けられます。',
            image: 'https://readdy.ai/api/search-image?query=trading%20rules%20strategy%20plan%20discipline%20stop%20loss%20management%20clean%20modern%20design&width=800&height=600&seq=minimum-step4-001&orientation=landscape',
            url: '/minimum-amount',
          },
        ],
      }),
      createFAQStructuredData([
        {
          question: 'FXは最低いくらから始められますか？',
          answer: '国内FX会社なら数千円〜1万円程度から始められます。レバレッジ25倍を使えば、1,000通貨取引で約6,000円の証拠金で取引可能です。ただし、余裕を持って1〜3万円程度の資金で始めることをおすすめします。',
          url: '/minimum-amount',
        },
        {
          question: '少額でFXを始めるメリットは何ですか？',
          answer: '少額取引の最大のメリットは、リスクを抑えながら実践経験を積めることです。大きな損失を避けやすく、メンタルへの負担も少ないため、初心者が値動きや注文方法に慣れる練習期間として最適です。',
          url: '/minimum-amount',
        },
        {
          question: '少額取引の注意点は何ですか？',
          answer: 'レバレッジのかけすぎに注意が必要です。少額でもフルレバレッジに近い取引をすると、わずかな値動きで大きな損失につながります。余裕資金で行う、レバレッジを抑える、損切りルールを決めるといった基本を守ることが重要です。',
          url: '/minimum-amount',
        },
        {
          question: '初心者におすすめのFX口座は？',
          answer: '1,000通貨単位で取引できる国内FX会社がおすすめです。DMM FXは1,000通貨対応、スマホアプリが使いやすく、国内大手で安心感があります。スプレッドも業界最狭水準で、初心者向けサポートも充実しています。',
          url: '/minimum-amount',
        }
      ]),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX入門', url: '/fx-guide' },
        { name: '少額でいくらから始められる？', url: '/minimum-amount' },
      ]),
    ],
  });

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
      <p className="text-sm text-gray-500 mt-3">口座開設・維持費無料 / 最短5分で申込完了</p>
    </div>
  );

  const merits = [
    {
      icon: 'ri-shield-check-line',
      title: '大きな損失を避けやすい',
      description: '少額なら万が一失敗しても、生活に大きな影響を与えません。リスクを最小限に抑えながら取引できます。'
    },
    {
      icon: 'ri-emotion-happy-line',
      title: 'メンタルへの負担が少ない',
      description: '大金を動かすプレッシャーがないため、冷静な判断がしやすくなります。初心者には特に重要なポイントです。'
    },
    {
      icon: 'ri-book-open-line',
      title: '実践しながら学べる',
      description: 'デモトレードと違い、実際のお金を使うことで真剣に取り組めます。値動きや注文方法に慣れる最適な練習期間になります。'
    }
  ];

  const cautions = [
    {
      icon: 'ri-alert-line',
      title: 'レバレッジのかけすぎ',
      description: 'フルレバレッジに近い取引は、わずかな値動きで大きな損失につながります。',
      solution: '最初は5倍以下の低レバレッジから始めましょう。'
    },
    {
      icon: 'ri-funds-line',
      title: 'ロスカットのリスク',
      description: '証拠金維持率が一定以下になると、自動的に決済されてしまいます。',
      solution: '余裕を持った証拠金で取引し、証拠金維持率を常に確認しましょう。'
    },
    {
      icon: 'ri-emotion-unhappy-line',
      title: '感情的な取引',
      description: '少額でも損失が続くと、取り返そうと無理な取引をしてしまがちです。',
      solution: '事前に損切りルールを決めて、必ず守るようにしましょう。'
    }
  ];

  const steps = [
    {
      number: '01',
      title: '1,000通貨対応口座を選ぶ',
      description: '少額取引に対応したFX会社で口座を開設します。',
      icon: 'ri-bank-line'
    },
    {
      number: '02',
      title: '余裕資金を準備する',
      description: '生活費とは別の余裕資金を1〜3万円程度用意します。',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      number: '03',
      title: 'レバレッジを抑える',
      description: '最初は5倍以下の低レバレッジで取引します。',
      icon: 'ri-scales-3-line'
    },
    {
      number: '04',
      title: '損切りルールを決める',
      description: '事前に損失許容額を決めて、ルールを守ります。',
      icon: 'ri-shield-check-line'
    }
  ];

  const faqs = [
    {
      question: 'FXは最低いくらから始められますか？',
      answer: '国内FX会社なら数千円〜1万円程度から始められます。レバレッジ25倍を使えば、1,000通貨取引で約6,000円の証拠金で取引可能です。ただし、余裕を持って1〜3万円程度の資金で始めることをおすすめします。'
    },
    {
      question: '少額でFXを始めるメリットは何ですか？',
      answer: '少額取引の最大のメリットは、リスクを抑えながら実践経験を積めることです。大きな損失を避けやすく、メンタルへの負担も少ないため、初心者が値動きや注文方法に慣れる練習期間として最適です。'
    },
    {
      question: '少額取引の注意点は何ですか？',
      answer: 'レバレッジのかけすぎに注意が必要です。少額でもフルレバレッジに近い取引をすると、わずかな値動きで大きな損失につながります。余裕資金で行う、レバレッジを抑える、損切りルールを決めるといった基本を守ることが重要です。'
    },
    {
      question: '初心者におすすめのFX口座は？',
      answer: '1,000通貨単位で取引できる国内FX会社がおすすめです。DMM FXは1,000通貨対応、スマホアプリが使いやすく、国内大手で安心感があります。スプレッドも業界最狭水準で、初心者向けサポートも充実しています。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX入門', url: '/fx-guide' },
            { name: '少額でいくらから始められる？', url: '/minimum-amount' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-20 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 overflow-hidden">
        {/* 背景装飾 */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <LazyImage
            src="https://readdy.ai/api/search-image?query=abstract%20financial%20growth%20chart%20coins%20savings%20money%20investment%20minimal%20clean%20design%20soft%20teal%20green%20gradient%20background%20geometric%20shapes&width=800&height=600&seq=minimum-bg-001&orientation=landscape"
            alt="背景装飾"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-4 md:mb-6 shadow-sm">
                <i className="ri-money-dollar-circle-line text-teal-600 text-base md:text-lg"></i>
                <span className="text-xs md:text-sm font-medium text-gray-700">少額から始める</span>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
                FXは少額いくらから始められる？
              </h1>
              
              <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                「FXってお金がたくさん必要なんじゃないの？」と不安に感じていませんか。<br className="hidden md:block" />
                実は、FXは数千円〜数万円の少額から始めることも可能です。
              </p>

              {/* CTA①：冒頭 */}
              <CTAButton />
            </div>
            
            {/* ヒーロー画像 */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="w-full h-80 rounded-2xl overflow-hidden shadow-2xl">
                  <LazyImage
                    src="https://readdy.ai/api/search-image?query=young%20japanese%20person%20holding%20smartphone%20checking%20investment%20app%20with%20small%20coins%20and%20piggy%20bank%20on%20desk%20minimal%20clean%20modern%20bright%20office%20setting%20warm%20lighting%20professional%20photography&width=600&height=400&seq=minimum-hero-002&orientation=landscape"
                    alt="少額からFXを始めるイメージ"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                {/* フローティングカード */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center">
                      <i className="ri-coin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">最低必要資金</p>
                      <p className="text-xl font-bold text-teal-600">約6,000円〜</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 最低資金 Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">FXは最低いくら必要？</h2>
            <p className="text-gray-600 text-sm md:text-base">
              <Highlight variant="marker" color="red">国内FX会社なら数千円〜始められるケースがあります</Highlight>
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* レバレッジ図解 */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-start mb-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-lightbulb-line text-white text-xl md:text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">レバレッジの仕組み</h3>
                      <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                        FXは「レバレッジ」という仕組みにより、少ない資金で大きな金額の取引ができます。<br />
                        レバレッジとは、預けた証拠金（担保）をもとに、<Highlight variant="bold" color="red">最大25倍まで取引できる仕組み</Highlight>のことです。
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* レバレッジ図解イメージ */}
                <div className="w-full h-48 md:h-56 rounded-xl overflow-hidden">
                  <LazyImage
                    src="https://readdy.ai/api/search-image?query=leverage%20concept%20illustration%20showing%20small%20amount%20multiplied%20into%20larger%20amount%20with%20arrows%20and%20multiplication%20symbol%20clean%20minimal%20infographic%20style%20teal%20green%20color%20scheme%20white%20background&width=500&height=300&seq=minimum-leverage-001&orientation=landscape"
                    alt="レバレッジの仕組み図解"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* 計算例 */}
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 mt-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center text-base md:text-lg">
                  <i className="ri-calculator-line text-teal-600 mr-2"></i>
                  具体例で計算してみよう
                </h4>
                
                {/* ビジュアル計算図 */}
                <div className="bg-white rounded-xl p-4 md:p-6 mb-4">
                  <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                    {/* 証拠金 */}
                    <div className="text-center">
                      <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
                        <span className="text-white font-bold text-lg md:text-xl">¥6,000</span>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">証拠金</p>
                    </div>
                    
                    {/* 矢印と倍率 */}
                    <div className="flex items-center">
                      <i className="ri-arrow-right-line text-teal-500 text-2xl md:text-3xl hidden md:block"></i>
                      <i className="ri-arrow-down-line text-teal-500 text-2xl md:text-3xl md:hidden"></i>
                      <div className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-bold mx-2">
                        ×25倍
                      </div>
                      <i className="ri-arrow-right-line text-teal-500 text-2xl md:text-3xl hidden md:block"></i>
                      <i className="ri-arrow-down-line text-teal-500 text-2xl md:text-3xl md:hidden"></i>
                    </div>
                    
                    {/* 取引可能額 */}
                    <div className="text-center">
                      <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
                        <span className="text-white font-bold text-lg md:text-2xl">¥150,000</span>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">取引可能額</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">為替レート</p>
                    <p className="text-lg md:text-xl font-bold text-gray-900">1ドル = 150円</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">取引単位</p>
                    <p className="text-lg md:text-xl font-bold text-teal-600">1,000通貨</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">必要証拠金</p>
                    <p className="text-lg md:text-xl font-bold text-emerald-600">約6,000円</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500">
                <div className="flex items-start">
                  <i className="ri-information-line text-amber-600 text-lg md:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      <strong className="text-gray-900">つまり、理論上は1万円前後からでも取引可能</strong>ということになります。<br />
                      ただし、価格が逆に動くと損失も発生します。<Highlight variant="bold" color="red">余裕資金で始めることが大切</Highlight>です。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 資金別取引可能額グラフ */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-bar-chart-grouped-line text-teal-600 mr-2"></i>
                資金別の取引可能額（レバレッジ25倍の場合）
              </h3>
              
              {/* 棒グラフ風ビジュアル */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-20 md:w-24 text-right pr-4">
                    <span className="text-sm md:text-base font-semibold text-gray-700">5,000円</span>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 md:h-10 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-400 to-teal-500 h-full rounded-full flex items-center justify-end pr-3" style={{ width: '20%' }}>
                      <span className="text-white text-xs md:text-sm font-bold">12.5万円</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-20 md:w-24 text-right pr-4">
                    <span className="text-sm md:text-base font-semibold text-gray-700">1万円</span>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 md:h-10 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-500 to-emerald-500 h-full rounded-full flex items-center justify-end pr-3" style={{ width: '40%' }}>
                      <span className="text-white text-xs md:text-sm font-bold">25万円</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-20 md:w-24 text-right pr-4">
                    <span className="text-sm md:text-base font-semibold text-gray-700">3万円</span>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 md:h-10 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full rounded-full flex items-center justify-end pr-3" style={{ width: '60%' }}>
                      <span className="text-white text-xs md:text-sm font-bold">75万円</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-20 md:w-24 text-right pr-4">
                    <span className="text-sm md:text-base font-semibold text-gray-700">5万円</span>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 md:h-10 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-600 to-green-600 h-full rounded-full flex items-center justify-end pr-3" style={{ width: '80%' }}>
                      <span className="text-white text-xs md:text-sm font-bold">125万円</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-20 md:w-24 text-right pr-4">
                    <span className="text-sm md:text-base font-semibold text-amber-600">10万円</span>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-8 md:h-10 overflow-hidden">
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 h-full rounded-full flex items-center justify-end pr-3" style={{ width: '100%' }}>
                      <span className="text-white text-xs md:text-sm font-bold">250万円</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-4 text-center">※ 実際の取引では余裕を持った資金管理が重要です</p>
            </div>

            {/* レバレッジ詳細へのリンク */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 md:p-8 text-white mb-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-alert-line text-white text-xl md:text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold mb-3">少額から始める場合でも守るべき3つの基本</h3>
                  <p className="text-sm md:text-base text-teal-50 leading-relaxed">
                    レバレッジの仕組みやリスク管理について、さらに詳しく知りたい方はこちら。<br />
                    → <Link to="/leverage" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「FXのレバレッジとは？仕組みとリスク管理を徹底解説」</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* メリット Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">少額FXのメリット</h2>
            <p className="text-gray-600 text-sm md:text-base">
              <Highlight variant="marker" color="red">リスクを抑えながら経験を積めることが最大のメリット</Highlight>
            </p>
          </div>

          {/* メリット画像付きカード */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
              <div className="w-full h-36 md:h-40">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=shield%20protection%20safety%20security%20concept%20minimal%20illustration%20with%20coins%20and%20money%20safe%20investment%20low%20risk%20clean%20modern%20design%20teal%20green%20accent&width=400&height=250&seq=minimum-merit1-001&orientation=landscape"
                  alt="大きな損失を避けやすい"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4 -mt-10 relative z-10 shadow-lg">
                  <i className="ri-shield-check-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">大きな損失を避けやすい</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">少額なら万が一失敗しても、生活に大きな影響を与えません。リスクを最小限に抑えながら取引できます。</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
              <div className="w-full h-36 md:h-40">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=calm%20relaxed%20person%20meditation%20peace%20of%20mind%20stress%20free%20mental%20health%20concept%20minimal%20illustration%20soft%20colors%20clean%20modern%20design&width=400&height=250&seq=minimum-merit2-001&orientation=landscape"
                  alt="メンタルへの負担が少ない"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4 -mt-10 relative z-10 shadow-lg">
                  <i className="ri-emotion-happy-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">メンタルへの負担が少ない</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">大金を動かすプレッシャーがないため、冷静な判断がしやすくなります。初心者には特に重要なポイントです。</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
              <div className="w-full h-36 md:h-40">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=learning%20studying%20practice%20training%20concept%20with%20books%20and%20charts%20growth%20progress%20minimal%20illustration%20clean%20modern%20design%20education&width=400&height=250&seq=minimum-merit3-001&orientation=landscape"
                  alt="実践しながら学べる"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4 -mt-10 relative z-10 shadow-lg">
                  <i className="ri-book-open-line text-white text-xl md:text-2xl"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">実践しながら学べる</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">デモトレードと違い、実際のお金を使うことで真剣に取り組めます。値動きや注文方法に慣れる最適な練習期間になります。</p>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-10 max-w-4xl mx-auto bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border-l-4 border-teal-500">
            <div className="flex items-start">
              <i className="ri-star-line text-teal-600 text-xl md:text-2xl mr-3 mt-0.5 flex-shrink-0"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2 text-base md:text-lg">特に初心者におすすめ</h4>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  いきなり大きな金額を動かすと冷静な判断が難しくなります。少額なら、値動きや注文方法に慣れる練習期間として活用できます。<br />
                  また、実際のお金を使うことで、デモトレードよりも真剣に取り組める傾向があります。<strong>「まずは経験」ができる点は、少額FXの大きな強み</strong>です。
                </p>
              </div>
            </div>
          </div>

          {/* CTA②：メリット後 */}
          <div className="mt-10 md:mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* 注意点 Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">少額で始める注意点</h2>
            <p className="text-gray-600 text-sm md:text-base">
              <Highlight variant="marker" color="red">レバレッジのかけすぎに注意が必要</Highlight>
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* 警告セクション with 画像 */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 md:p-8 border-l-4 border-red-500 mb-8">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-start">
                    <i className="ri-alert-line text-red-600 text-2xl md:text-3xl mr-4 mt-0.5 flex-shrink-0"></i>
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">少額だからといって、常に安全とは限りません</h3>
                      <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                        レバレッジを高くすると、値動きが少しでも大きな損益につながります。
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-bold text-gray-900 mb-2 text-sm md:text-base">例えば</h4>
                        <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                          1万円の証拠金でフルレバレッジ（25倍）に近い取引をすると、<strong className="text-red-600">数十銭の変動でも大きな損失</strong>になる可能性があります。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="w-full h-40 md:h-48 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=warning%20caution%20alert%20risk%20danger%20concept%20illustration%20with%20exclamation%20mark%20and%20downward%20chart%20minimal%20clean%20design%20red%20orange%20color%20scheme&width=400&height=300&seq=minimum-caution-001&orientation=landscape"
                      alt="注意点イメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* リスク比較図 */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-scales-3-line text-red-500 mr-2"></i>
                レバレッジによるリスクの違い
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* 低レバレッジ */}
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 border-2 border-teal-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-thumb-up-line text-white text-lg"></i>
                    </div>
                    <h4 className="font-bold text-teal-700 text-base md:text-lg">低レバレッジ（5倍）</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">証拠金</span>
                      <span className="font-bold text-gray-900">1万円</span>
                    </div>
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">取引額</span>
                      <span className="font-bold text-gray-900">5万円</span>
                    </div>
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">1円変動時の損益</span>
                      <span className="font-bold text-teal-600">±500円</span>
                    </div>
                  </div>
                  <p className="text-xs text-teal-700 mt-4 bg-teal-100 rounded-lg p-2">
                    <i className="ri-check-line mr-1"></i>
                    余裕を持って取引できる
                  </p>
                </div>
                
                {/* 高レバレッジ */}
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-5 border-2 border-red-200">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-alert-line text-white text-lg"></i>
                    </div>
                    <h4 className="font-bold text-red-700 text-base md:text-lg">高レバレッジ（25倍）</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">証拠金</span>
                      <span className="font-bold text-gray-900">1万円</span>
                    </div>
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">取引額</span>
                      <span className="font-bold text-gray-900">25万円</span>
                    </div>
                    <div className="flex justify-between items-center bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">1円変動時の損益</span>
                      <span className="font-bold text-red-600">±2,500円</span>
                    </div>
                  </div>
                  <p className="text-xs text-red-700 mt-4 bg-red-100 rounded-lg p-2">
                    <i className="ri-close-line mr-1"></i>
                    ロスカットのリスクが高い
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
              {cautions.map((caution, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 md:p-6 border border-gray-200">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-red-100 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                    <i className={`${caution.icon} text-red-600 text-xl md:text-2xl`}></i>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">{caution.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-3">{caution.description}</p>
                  <div className="bg-teal-50 rounded-lg p-3">
                    <div className="flex items-start">
                      <i className="ri-lightbulb-line text-teal-600 text-sm md:text-base mr-2 mt-0.5 flex-shrink-0"></i>
                      <p className="text-xs leading-relaxed">
                        <Highlight variant="bold" color="red" className="text-xs">解決策:</Highlight> {caution.solution}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 md:p-8 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-4 flex items-center">
                <i className="ri-shield-check-line mr-3"></i>
                少額で始める場合でも守るべき3つの基本
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-3">
                    <span className="font-bold text-lg">1</span>
                  </div>
                  <h4 className="font-bold mb-2 text-base md:text-lg">余裕資金で行う</h4>
                  <p className="text-sm text-teal-50">生活費とは別の資金で取引する</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-3">
                    <span className="font-bold text-lg">2</span>
                  </div>
                  <h4 className="font-bold mb-2 text-base md:text-lg">レバレッジを抑える</h4>
                  <p className="text-sm text-teal-50">最初は5倍以下から始める</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-3">
                    <span className="font-bold text-lg">3</span>
                  </div>
                  <h4 className="font-bold mb-2 text-base md:text-lg">損切りルールを決める</h4>
                  <p className="text-sm text-teal-50">事前に決めたルールを必ず守る</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* おすすめ口座 Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">少額に向く口座</h2>
            <p className="text-gray-600 text-sm md:text-base">
              <Highlight variant="marker" color="red">1,000通貨単位で取引できる国内FX会社が向いています</Highlight>
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* 取引単位比較 */}
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-exchange-line text-teal-600 mr-2"></i>
                取引単位による必要資金の違い
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* 1万通貨 */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-close-line text-gray-600 text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-gray-700 text-lg">1万通貨単位</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">最低必要資金</span>
                      <span className="font-bold text-gray-900">約6万円</span>
                    </div>
                    <div className="flex justify-between bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">1円変動時</span>
                      <span className="font-bold text-gray-900">±1万円</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">初心者にはハードルが高い</p>
                </div>
                
                {/* 1,000通貨 */}
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 border-2 border-teal-300">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-check-line text-white text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-teal-700 text-lg">1,000通貨単位</h4>
                    <span className="inline-block bg-teal-500 text-white text-xs px-2 py-1 rounded-full mt-1">おすすめ</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">最低必要資金</span>
                      <span className="font-bold text-teal-600">約6,000円</span>
                    </div>
                    <div className="flex justify-between bg-white rounded-lg p-3">
                      <span className="text-sm text-gray-600">1円変動時</span>
                      <span className="font-bold text-teal-600">±1,000円</span>
                    </div>
                  </div>
                  <p className="text-xs text-teal-700 mt-3 text-center font-semibold">初心者でも始めやすい！</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-alert-line text-white text-xl md:text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">FX会社によって最低取引単位が異なります</h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    FX会社によっては、1万通貨からしか取引できない場合もあります。<br />
                    初心者には、<Highlight variant="bold" color="red">1,000通貨単位に対応している口座</Highlight>がおすすめです。
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 text-base md:text-lg">1,000通貨対応口座をおすすめする理由</h4>
                <div className="space-y-3">
                  <div className="flex items-start bg-white rounded-lg p-3">
                    <i className="ri-check-line text-teal-600 text-lg md:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm md:text-base text-gray-700">必要資金を抑えられる</p>
                  </div>
                  <div className="flex items-start bg-white rounded-lg p-3">
                    <i className="ri-check-line text-teal-600 text-lg md:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm md:text-base text-gray-700">損失リスクを小さくできる</p>
                  </div>
                  <div className="flex items-start bg-white rounded-lg p-3">
                    <i className="ri-check-line text-teal-600 text-lg md:text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                    <p className="text-sm md:text-base text-gray-700">資金管理がしやすい</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 md:p-8 text-white mb-8">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-start mb-6">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-star-line text-white text-2xl md:text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold mb-3">FX初心者におすすめ：DMM FX</h3>
                      <p className="text-sm md:text-base text-teal-50 leading-relaxed mb-4">
                        国内大手FX会社の中には、スマホアプリが使いやすく、初心者向けのサポート体制が整っている会社もあります。
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <i className="ri-check-double-line text-xl mb-1"></i>
                      <p className="text-xs md:text-sm font-bold">1,000通貨対応</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <i className="ri-smartphone-line text-xl mb-1"></i>
                      <p className="text-xs md:text-sm font-bold">使いやすいアプリ</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <i className="ri-shield-check-line text-xl mb-1"></i>
                      <p className="text-xs md:text-sm font-bold">国内大手で安心</p>
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-2">
                  <div className="w-full h-40 md:h-48 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=smartphone%20trading%20app%20interface%20with%20charts%20and%20graphs%20modern%20clean%20design%20professional%20financial%20application%20mobile%20device%20mockup&width=400&height=300&seq=minimum-app-001&orientation=landscape"
                      alt="DMM FXアプリイメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 初心者ガイドへのリンク */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-emerald-500 shadow-md">
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                DMM FXが初心者におすすめな理由を詳しく知りたい方はこちら。<br />
                → <Link to="/beginner-guide" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXは初心者でも大丈夫？メリット・デメリットを徹底解説」</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 始め方 Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">少額で始める4ステップ</h2>
            <p className="text-gray-600 text-sm md:text-base">安全に少額取引を始める方法</p>
          </div>

          {/* ステップ画像付き */}
          <div className="max-w-5xl mx-auto space-y-6 md:space-y-8">
            {/* ステップ1 */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-start">
                    <div className="text-4xl md:text-5xl font-bold text-teal-100 mr-4">01</div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-bank-line text-white text-xl"></i>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">1,000通貨対応口座を選ぶ</h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        少額取引に対応したFX会社で口座を開設します。1,000通貨単位なら必要資金を抑えられます。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="w-full h-36 md:h-44 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=online%20account%20registration%20form%20on%20laptop%20screen%20with%20checkmarks%20and%20verification%20icons%20clean%20modern%20interface%20design%20professional&width=400&height=280&seq=minimum-step1-002&orientation=landscape"
                      alt="口座選びイメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ2 */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-2 order-2 lg:order-1">
                  <div className="w-full h-36 md:h-44 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=piggy%20bank%20with%20coins%20savings%20money%20jar%20budget%20planning%20concept%20minimal%20clean%20design%20soft%20lighting%20warm%20colors&width=400&height=280&seq=minimum-step2-002&orientation=landscape"
                      alt="資金準備イメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="lg:col-span-3 order-1 lg:order-2">
                  <div className="flex items-start">
                    <div className="text-4xl md:text-5xl font-bold text-teal-100 mr-4">02</div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">余裕資金を準備する</h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        生活費とは別の余裕資金を用意します。初心者は1〜3万円程度から始めるのがおすすめです。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ3 */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-3">
                  <div className="flex items-start">
                    <div className="text-4xl md:text-5xl font-bold text-teal-100 mr-4">03</div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-scales-3-line text-white text-xl"></i>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">レバレッジを抑える</h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        最初は5倍以下の低レバレッジで取引します。リスクを抑えながら経験を積めます。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="w-full h-36 md:h-44 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=balance%20scale%20with%20risk%20and%20reward%20concept%20minimal%20illustration%20safety%20first%20approach%20careful%20investment%20clean%20modern%20design&width=400&height=280&seq=minimum-step3-002&orientation=landscape"
                      alt="レバレッジ管理イメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ4 */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg">
              <div className="grid lg:grid-cols-5 gap-6 items-center">
                <div className="lg:col-span-2 order-2 lg:order-1">
                  <div className="w-full h-36 md:h-44 rounded-xl overflow-hidden">
                    <LazyImage
                      src="https://readdy.ai/api/search-image?query=checklist%20with%20rules%20and%20guidelines%20document%20planning%20strategy%20concept%20minimal%20clean%20design%20professional%20business%20setting&width=400&height=280&seq=minimum-step4-002&orientation=landscape"
                      alt="ルール設定イメージ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="lg:col-span-3 order-1 lg:order-2">
                  <div className="flex items-start">
                    <div className="text-4xl md:text-5xl font-bold text-teal-100 mr-4">04</div>
                    <div>
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3">
                        <i className="ri-shield-check-line text-white text-xl"></i>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">損切りルールを決める</h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        事前に損失許容額を決めて、ルールを守って取引します。感情的な判断を避けられます。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA③：ステップ後 */}
          <div className="text-center mt-10 md:mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">よくある質問</h2>
            <p className="text-gray-600 text-sm md:text-base">少額FXに関する疑問を解決</p>
          </div>

          <div className="space-y-3 md:space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm md:text-base">Q</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2 md:mb-3">{faq.question}</h3>
                    <div className="flex items-start space-x-3 md:space-x-4">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 font-bold text-sm md:text-base">A</span>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* まとめ Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">まとめ</h2>
          </div>

          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 shadow-lg mb-8">
            {/* まとめ画像 */}
            <div className="w-full h-48 md:h-56 rounded-xl overflow-hidden mb-6">
              <LazyImage
                src="https://readdy.ai/api/search-image?query=success%20achievement%20goal%20reached%20concept%20with%20person%20celebrating%20small%20wins%20growth%20progress%20minimal%20illustration%20clean%20modern%20design%20teal%20green%20accent&width=800&height=400&seq=minimum-summary-001&orientation=landscape"
                alt="まとめイメージ"
                className="w-full h-full object-cover"
              />
            </div>
            
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-6">
              FXは<Highlight variant="bold" color="red">数千円〜1万円程度の少額から始めることも可能</Highlight>です。<br />
              特に初心者は、いきなり大きな金額を投入せず、少額で経験を積むのが安心と言えるでしょう。
            </p>

            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-l-4 border-red-500 mb-6">
              <div className="flex items-start">
                <i className="ri-alert-line text-red-600 text-xl md:text-2xl mr-3 mt-0.5 flex-shrink-0"></i>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">ただし注意が必要</h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    レバレッジや資金管理を誤ると、少額でも大きな損失につながる可能性があります。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-base md:text-lg flex items-center">
                <i className="ri-checkbox-circle-line text-teal-600 mr-2"></i>
                まずは、この3つを意識しましょう
              </h3>
              <div className="space-y-3">
                <div className="flex items-start bg-white rounded-lg p-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <p className="text-sm md:text-base text-gray-700"><strong>余裕資金で始める</strong> - 生活費とは別の資金で</p>
                </div>
                <div className="flex items-start bg-white rounded-lg p-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <p className="text-sm md:text-base text-gray-700"><strong>低レバレッジを意識する</strong> - 最初は5倍以下から</p>
                </div>
                <div className="flex items-start bg-white rounded-lg p-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <p className="text-sm md:text-base text-gray-700"><strong>1,000通貨対応口座を選ぶ</strong> - 必要資金を抑えられる</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-base md:text-lg text-gray-700 mb-6">
              この3つを意識して、<Highlight variant="bold" color="red">無理のないスタート</Highlight>を切りましょう。
            </p>
          </div>
        </div>
      </section>

      {/* 関連記事 Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">関連記事</h2>
            <p className="text-gray-600 text-sm md:text-base">もっと詳しく知りたい方へ</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            <Link to="/fx-basics" className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-full h-24 md:h-32">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=forex%20trading%20basics%20introduction%20beginner%20guide%20concept%20with%20charts%20and%20currency%20symbols%20minimal%20clean%20design&width=300&height=200&seq=minimum-related1-001&orientation=landscape"
                  alt="FX超入門ガイド"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">FX超入門ガイド</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">FXの基本から学びたい方へ</p>
                <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
                </div>
              </div>
            </Link>

            <Link to="/leverage" className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-full h-24 md:h-32">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=leverage%20concept%20balance%20scale%20risk%20management%20trading%20minimal%20clean%20design%20professional&width=300&height=200&seq=minimum-related2-001&orientation=landscape"
                  alt="レバレッジ解説"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">レバレッジ解説</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">仕組みとリスク管理を詳しく</p>
                <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
                </div>
              </div>
            </Link>

            <Link to="/beginner-guide" className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-full h-24 md:h-32">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=beginner%20friendly%20guide%20tutorial%20concept%20with%20helpful%20icons%20and%20checkmarks%20minimal%20clean%20design&width=300&height=200&seq=minimum-related3-001&orientation=landscape"
                  alt="初心者ガイド"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">初心者ガイド</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">DMM FXのメリット・デメリット</p>
                <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
                </div>
              </div>
            </Link>

            <Link to="/account-opening" className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-full h-24 md:h-32">
                <LazyImage
                  src="https://readdy.ai/api/search-image?query=account%20opening%20registration%20process%20steps%20concept%20with%20form%20and%20documents%20minimal%20clean%20design&width=300&height=200&seq=minimum-related4-001&orientation=landscape"
                  alt="口座開設手順"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 md:p-6">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">口座開設手順</h3>
                <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">画像付きで詳しく解説</p>
                <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-teal-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            少額から安心してFXを始めよう
          </h2>
          <p className="text-sm md:text-lg text-white/90 mb-6 md:mb-8 leading-relaxed">
            DMM FXなら1,000通貨から取引可能。<br className="hidden md:block" />
            初心者向けサポートも充実しているので、安心して始められます。
          </p>
          
          {/* CTA④：最終CTA */}
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold mb-4 animate-pulse">
            <i className="ri-gift-line"></i>
            <span>👉 今だけ最大50万円キャッシュバック</span>
          </div>
          <div className="block mb-3">
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-6 md:px-8 py-4 md:py-5 rounded-xl text-base md:text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
            >
              👉 今だけ最大50万円キャッシュバック<br />
              無料でDMM FX口座を開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
          <p className="text-xs md:text-sm text-white/80">口座開設・維持費無料 / 最短5分で申込完了</p>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}